import { Group, Menu, Portal, Text, Tooltip } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconBackspace, IconDotsVertical, IconPencil, IconTrashX } from '@tabler/icons-react';
import { useApi } from 'api/api-context';
import ActionIcon from 'components/ActionIcon';
import Button from 'components/Button';
import { useConfirm } from 'components/modals/message/MessageProvider';
import panic from 'errors/panic';
import { useResponsiveDesign } from 'hooks/use-responsive-design';
import { Link } from 'react-router-dom';
import { EDIT_HIERARCHY_PAGE_PATH } from 'routes/paths';
import { SUCCESS_NOTIFICATION_COLOR } from 'utils/constants';

interface HierarchyActionsProps {
  hierarchyId: number;
  hierarchyName: string;
  status: boolean;
  isDefault: boolean;
  canDeactivate: boolean;
  canActivate: boolean;
  onChanged: (change: { status: boolean }) => void;
}

/** Responsive lifecycle actions for a hierarchy table row. */
export default function HierarchyActions({
  hierarchyId,
  hierarchyName,
  status,
  isDefault,
  canDeactivate,
  canActivate,
  onChanged,
}: HierarchyActionsProps) {
  const { getAction } = useApi();
  const { confirm } = useConfirm();
  const { tableActionMode, isMobile } = useResponsiveDesign();
  const compact = tableActionMode !== 'desktop';

  /** Applies an allowed retained lifecycle transition. */
  const setStatus = (nextStatus: boolean) =>
    confirm({
      title: nextStatus ? 'Zaradiť hierarchiu' : 'Vyradiť hierarchiu',
      content: `Naozaj chcete ${nextStatus ? 'zaradiť' : 'vyradiť'} hierarchiu ${hierarchyName}?`,
      onConfirm: () =>
        getAction('HierarchySetStatus')({
          parameters: { hierarchyId: String(hierarchyId) },
          payload: { status: nextStatus },
        })
          .then(() => {
            onChanged({ status: nextStatus });
            notifications.show({
              title: nextStatus ? 'Hierarchia bola zaradená' : 'Hierarchia bola vyradená',
              message: `Stav hierarchie ${hierarchyName} bol úspešne uložený.`,
              color: SUCCESS_NOTIFICATION_COLOR,
            });
          })
          .catch(panic),
    });

  return (
    <Group spacing={12} noWrap>
      {!compact && (
        <Button
          component={Link}
          to={EDIT_HIERARCHY_PAGE_PATH.insert({ hierarchyId })}
          variant="secondary"
          w={118}
          disabled={!status}
          leftIcon={<IconPencil />}
        >
          Editovať
        </Button>
      )}
      {compact && (
        <Tooltip label="Editovať">
          <ActionIcon
            component={Link}
            to={EDIT_HIERARCHY_PAGE_PATH.insert({ hierarchyId })}
            variant="secondary"
            disabled={!status}
          >
            <IconPencil />
          </ActionIcon>
        </Tooltip>
      )}
      <Menu position="bottom-end">
        <Menu.Target>
          <ActionIcon variant="tertiary" size={isMobile ? 'sm' : 'md'}>
            <IconDotsVertical />
          </ActionIcon>
        </Menu.Target>
        <Portal>
          <Menu.Dropdown>
            <Menu.Label>
              <Text maw={160} truncate>
                {hierarchyName}
              </Text>
            </Menu.Label>
            {isDefault && (
              <Menu.Label>
                <Text maw={200}>Predvolená hierarchia je stála a nemožno ju vyradiť</Text>
              </Menu.Label>
            )}
            {canDeactivate && (
              <Menu.Item color="red" icon={<IconTrashX size={20} />} onClick={() => setStatus(false)}>
                Vyradiť
              </Menu.Item>
            )}
            {canActivate && (
              <Menu.Item icon={<IconBackspace size={20} />} onClick={() => setStatus(true)}>
                Zaradiť
              </Menu.Item>
            )}
          </Menu.Dropdown>
        </Portal>
      </Menu>
    </Group>
  );
}
